import { useRef, useState, type FormEvent } from "react";
import { Camera, Loader2, X } from "lucide-react";
import { de, type Receipt } from "@ainkauf/core";
import { leseBon } from "@/lib/assistant";
import { alsDatenUrl, verkleinern } from "@/lib/image";
import { supabaseConfigured } from "@/lib/supabase";

const euro = (cents: number) => (cents / 100).toLocaleString("de-DE", { style: "currency", currency: "EUR" });

/**
 * Abschluss eines Einkaufs: fragt nach dem Betrag, lässt ihn aber weg, wenn
 * jemand keine Lust hat. Ein erzwungenes Feld würde dazu führen, dass Leute
 * irgendetwas eintippen, und irgendetwas ist schlechter als nichts.
 *
 * Wer den Bon fotografiert, muss gar nichts tippen: Die Summe kommt vom Bon,
 * und die Einzelpreise landen an den Positionen des Einkaufs. Daraus wächst
 * der Preisverlauf, der später sagt, wo etwas günstiger war.
 */
export function FinishSheet({
  onFinish,
  onCancel,
}: {
  onFinish: (totalCents: number | null, receipt: Receipt | null) => Promise<void>;
  onCancel: () => void;
}) {
  const [betrag, setBetrag] = useState("");
  const [busy, setBusy] = useState(false);
  const [bon, setBon] = useState<Receipt | null>(null);
  const [liest, setLiest] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const dateiFeld = useRef<HTMLInputElement>(null);

  async function abschliessen(total: number | null) {
    setBusy(true);
    await onFinish(total, bon);
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    const zahl = Number.parseFloat(betrag.replace(",", "."));
    void abschliessen(Number.isFinite(zahl) && zahl > 0 ? Math.round(zahl * 100) : null);
  }

  async function bonLesen(datei: File) {
    setLiest(true);
    setFehler(null);
    try {
      const gelesen = await leseBon(await alsDatenUrl(await verkleinern(datei)));
      setBon(gelesen);
      if (gelesen.total_cents !== null) setBetrag((gelesen.total_cents / 100).toFixed(2).replace(".", ","));
    } catch {
      // Der genaue Fehler hilft hier niemandem weiter. Wichtig ist, dass der
      // Abschluss trotzdem möglich bleibt, notfalls mit getipptem Betrag.
      setFehler(de.trip.receiptFailed);
    } finally {
      setLiest(false);
    }
  }

  return (
    <div className="fixed inset-0 z-40 flex flex-col justify-end bg-ink/40" onClick={onCancel} role="presentation">
      <form
        onSubmit={submit}
        onClick={(event) => {
          event.stopPropagation();
        }}
        aria-label={de.trip.totalQuestion}
        data-testid="finish-sheet"
        className="flex flex-col gap-4 rounded-t-[var(--radius-xl)] bg-paper px-5 pt-5"
        style={{ paddingBottom: "calc(20px + env(safe-area-inset-bottom, 0px))" }}
      >
        <span aria-hidden className="mx-auto mb-1 h-1.5 w-12 rounded-full bg-paper-3" />
        <h2 className="text-xl">{de.trip.totalQuestion}</h2>
        <p className="text-sm text-ink-2">{de.trip.totalHint}</p>

        <label className="flex flex-col gap-1.5 text-sm text-ink-2">
          {de.trip.totalLabel}
          <input
            value={betrag}
            onChange={(event) => {
              setBetrag(event.target.value);
            }}
            inputMode="decimal"
            autoFocus
            placeholder="0,00"
            data-testid="finish-amount"
            className="num karte min-h-[64px] px-4 font-display text-[2rem] font-extrabold tracking-tight text-ink outline-none"
          />
        </label>

        {/* Der Bon ist ein Angebot, kein Schritt: Ohne Instanz gibt es ihn
            gar nicht, und mit Instanz kostet er einen Tipp. */}
        {supabaseConfigured() && (
          <div className="flex flex-col gap-2">
            <input
              ref={dateiFeld}
              type="file"
              accept="image/*"
              capture="environment"
              hidden
              data-testid="receipt-input"
              onChange={(event) => {
                const datei = event.target.files?.[0];
                event.target.value = "";
                if (datei) void bonLesen(datei);
              }}
            />

            {bon === null ? (
              <>
                <button
                  type="button"
                  disabled={liest || busy}
                  onClick={() => dateiFeld.current?.click()}
                  data-testid="receipt-scan"
                  className="knopf-leise inline-flex min-h-[52px] items-center justify-center gap-2 px-4 disabled:opacity-60"
                >
                  {liest ? <Loader2 size={18} className="animate-spin" aria-hidden /> : <Camera size={18} strokeWidth={2} aria-hidden />}
                  {liest ? de.trip.receiptReading : de.trip.receiptScan}
                </button>
                <p className="text-center text-xs text-ink-3">{de.trip.receiptHint}</p>
              </>
            ) : (
              <div
                className="karte kante flex items-center gap-3 py-3 pl-4 pr-2"
                style={{ ["--kante-farbe" as string]: "var(--herb)" }}
                data-testid="receipt-summary"
              >
                <span className="num min-w-0 flex-1 text-sm font-semibold text-ink-2">
                  {de.trip.receiptFound(bon.positions.length, euro(bon.total_cents ?? 0))}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setBon(null);
                  }}
                  aria-label={de.trip.receiptRemove}
                  data-testid="receipt-remove"
                  className="inline-flex size-11 shrink-0 items-center justify-center rounded-full text-ink-3 transition-transform active:scale-90"
                >
                  <X size={18} strokeWidth={2} aria-hidden />
                </button>
              </div>
            )}

            {fehler !== null && (
              <p role="alert" className="text-center text-sm text-accent">
                {fehler}
              </p>
            )}
          </div>
        )}

        <button
          type="submit"
          disabled={busy || liest}
          data-testid="finish-confirm"
          className="min-h-[60px] rounded-full bg-herb px-4 text-lg font-bold text-white shadow-[var(--shadow-float)] transition-transform active:scale-[0.98] disabled:opacity-60"
        >
          {de.trip.finish}
        </button>

        <button
          type="button"
          onClick={() => void abschliessen(null)}
          disabled={busy || liest}
          data-testid="finish-without-amount"
          className="min-h-[44px] text-sm text-ink-2 underline underline-offset-4"
        >
          {de.trip.skipTotal}
        </button>
      </form>
    </div>
  );
}
